// components/EntityCard.tsx - Compact Grid Layout
'use client';

import { BRAND, withBase } from '../lib/brand';
import React, { useMemo, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { StarFilled, EnvironmentOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { Entity } from '../types';
import FeatureIcon from './FeatureIcon';
import { ENTITY_CARD_SIZES } from '../constants/imageSizes';
import { getOptimizedEntityImage, getEntityImages } from '../utils/entityImage';
import { isFreeEntity } from '../utils/isFreeEntity';
import { displayName } from '../lib/displayName';
import { stripHtml } from '../lib/richText';
import { useCaPreview } from '../lib/caPreview';
import PlaceArt from './store/PlaceArt';
import { mediaThumb } from '../lib/mediaThumb';

interface EntityCardProps {
  entity: Entity;
  isSelected: boolean;
  onClick?: () => void;
  onViewDetails?: () => void;
  layout?: 'grid' | 'list';
  onAddToTrip?: (entity: Entity) => void;
  isInTrip?: boolean;
  /** ca_preview: one of the first results on screen; fetch its photo now. */
  eagerImage?: boolean;
}

// Default images for different campsite types (full path with app prefix for Shopify proxy)
const APP_PREFIX = BRAND.basePath; // '' for escapeto — concatenated below
const defaultImages = {
  beach: `${APP_PREFIX}/images/default-beach-camping.jpg`,
  forest: `${APP_PREFIX}/images/default-mountain-camping.jpg`,
  mountain: `${APP_PREFIX}/images/default-mountain-camping.jpg`,
  lake: `${APP_PREFIX}/images/default-lake-camping.jpg`,
  desert: `${APP_PREFIX}/images/default-desert-camping.jpg`,
  default: `${APP_PREFIX}/images/default-campsite-hero.jpg`
};

const hideBrokenImg = (e: React.SyntheticEvent<HTMLImageElement>) => {
  e.currentTarget.style.visibility = 'hidden';
};

// Helper to get default image based on features/name
function getDefaultImage(entity: Entity): string {
  const name = entity.name?.toLowerCase() || '';
  const features = entity.features?.map(f => f.desc?.toLowerCase() || '').join(' ') || '';
  const combined = name + ' ' + features;
  
  if (combined.includes('beach') || combined.includes('coast')) return defaultImages.beach;
  if (combined.includes('forest') || combined.includes('bush')) return defaultImages.forest;
  if (combined.includes('mountain') || combined.includes('alpine')) return defaultImages.mountain;
  if (combined.includes('lake') || combined.includes('river')) return defaultImages.lake;
  if (combined.includes('desert') || combined.includes('outback')) return defaultImages.desert;
  
  return defaultImages.default;
}

const EntityCard: React.FC<EntityCardProps> = ({
  entity,
  isSelected,
  onClick,
  onViewDetails,
  layout = 'grid',
  onAddToTrip,
  isInTrip = false,
  eagerImage = false,
}) => {
  const t = useTranslations('card');
  // i18n TODO (phase 2): image alt text, the derived campsiteType labels, and
  // the 'Australian Campsites' / 'Australia' data fallbacks are AU-specific
  // and left untranslated for phase 1 (they lean on entity data, not UI chrome).
  const safeName = entity.name || 'Australian Campsites';
  
  // Create SEO-friendly slug from entity name
  const slug = safeName
    .replace(/['''`]/g, '')        // Strip apostrophes and backticks first
    .replace(/&/g, 'and')          // Replace & with 'and'
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')  // Replace non-alphanumeric with hyphens
    .replace(/^-+|-+$/g, '')       // Remove leading/trailing hyphens
    .replace(/-+/g, '-');           // Replace multiple hyphens with single
  
  const entityUrl = withBase(`/camp-sites/${encodeURIComponent(slug)}`);

  const [imageError, setImageError] = useState(false);
  // Some listings (esp. harvested premium ones) use a brand LOGO as their hero
  // image. object-cover crops/oversizes a logo, so we detect a logo-shaped image
  // on load (small or near-square) and switch to a clean contained fit.
  const [heroFit, setHeroFit] = useState<'cover' | 'contain'>('cover');
  const handleHeroLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const w = img.naturalWidth, h = img.naturalHeight;
    if (!w || !h) return;
    const ar = w / h;
    // ca_preview photos arrive as sized thumbs: pixel size says nothing about logos.
    setHeroFit((!caPreview && Math.max(w, h) < 500) || (ar > 0.82 && ar < 1.25) ? 'contain' : 'cover');
  };

  const features = useMemo(() => ({
    amenities: entity.features?.filter(f => f.feature_type === 'TEXT') || [],
    icons: entity.features?.filter(f => f.feature_type === 'ICON') || [],
  }), [entity.features]);

  const fallbackImage = useMemo(() => getDefaultImage(entity), [entity]);
  // A listing with no photo of its own gets a typed tile (tent or caravan on
  // a tint), never the shared stock photo: three cards with the same campfire
  // read as placeholder content.
  const hasOwnImage = useMemo(
    () => !!getOptimizedEntityImage(entity, { width: 720, quality: 78, fallback: '' }),
    [entity]
  );

  // ca_preview: a photo-less listing shows the store's no-picture tile.
  const caPreview = useCaPreview();

  const primaryImage = useMemo(() => {
    if (imageError) {
      return fallbackImage;
    }
    return getOptimizedEntityImage(entity, {
      width: 720,
      quality: 78,
      fallback: fallbackImage,
    });
  }, [entity, imageError, fallbackImage]);

  const allImages = useMemo(() => {
    if (imageError) return [fallbackImage];
    return getEntityImages(entity, {
      mainWidth: 480,
      thumbWidth: 240,
      quality: 75,
      fallback: fallbackImage,
    });
  }, [entity, imageError, fallbackImage]);

  const totalGalleryCount = entity.gallery_images?.length ?? 0;
  const hasMosaic = allImages.length >= 2;

  // Get location info
  const location = useMemo(() => {
    if (entity.addresses && entity.addresses[0]) {
      const addr = entity.addresses[0];
      return `${addr.town || ''}, ${addr.state || ''}`.replace(/^, |, $/, '');
    }
    return 'Australia';
  }, [entity.addresses]);

  // Get key features for display (limit to 3 for compact grid)
  const keyFeatures = useMemo(() => {
    const priorityFeatures = [
      'Free Overnight Camp', 'Free Camping', 'Power', 'Powered Sites', 
      'Toilets', 'Showers', 'Pets Welcome', 'Pet Friendly', 'Drinking Water',
      'WiFi Internet', 'Swimming Pool', 'Cabins'
    ];
    
    const priority = features.icons.filter(f => 
      priorityFeatures.some(pf => f.desc?.toLowerCase().includes(pf.toLowerCase()))
    );
    
    const remaining = features.icons.filter(f => 
      !priorityFeatures.some(pf => f.desc?.toLowerCase().includes(pf.toLowerCase()))
    );
    
    return [...priority, ...remaining].slice(0, 3);
  }, [features.icons]);

  // Determine if campsite is free (shared rule — see utils/isFreeEntity).
  const isFree = useMemo(() => isFreeEntity(entity), [entity]);

  // Get campsite type for alt text
  const campsiteType = useMemo(() => {
    if (isFree) return 'Free Camping';
    if (entity.features?.some(f => f.desc?.toLowerCase().includes('caravan'))) return 'Caravan Park';
    if (entity.features?.some(f => f.desc?.toLowerCase().includes('cabin'))) return 'Holiday Park';
    return 'Campsite';
  }, [entity.features, isFree]);

  // Get a highlight feature for the card footer (most appealing attribute)
  const highlightFeature = useMemo(() => {
    const highlights = [
      'Pets Welcome', 'Pet Friendly', 'Swimming Pool', 'Beach Access',
      'Waterfront', 'WiFi Internet', 'Powered Sites', 'Hot Showers',
      'Cabins', 'Glamping', 'Bush Setting', 'River Access', 'Lake Access',
      'Fishing Spot', 'Playground', 'Camp Kitchen', 'BBQ',
    ];
    for (const h of highlights) {
      const match = entity.features?.find(f => f.desc?.toLowerCase().includes(h.toLowerCase()));
      if (match) return match.desc;
    }
    return null;
  }, [entity.features]);

  const isBookable = !!entity.booking_url;

  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (onClick) {
      e.preventDefault();
      e.stopPropagation();
      onClick();
    }
  };

  const handleImageError = () => {
    setImageError(true);
  };

  // COMPACT GRID LAYOUT
  const CardContent = () => (
    <article
      className={`group bg-white rounded-xl border overflow-hidden transition-all duration-200 cursor-pointer hover:shadow-lg hover:-translate-y-0.5 h-full flex flex-col ${
        isSelected ? 'ring-2 ring-accent shadow-md' : 'border-gray-200 hover:border-gray-300'
      }`}
      onClick={onClick ? handleCardClick : undefined}
    >
      {/* Premium header bar — full-width across the top so premium listings pop */}
      {entity.is_premium && (
        <div className="flex items-center justify-center gap-1.5 bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-400 py-1 text-[10px] font-extrabold uppercase tracking-[0.14em] text-amber-900">
          <StarFilled style={{ fontSize: '9px' }} />
          {t('premium')}
        </div>
      )}

      {/* Image area — 16:9 throughout; 4:3 made every card mostly photo */}
      <div className="relative w-full aspect-[16/9] overflow-hidden bg-gray-50 flex-shrink-0">
        {hasMosaic ? (
          /* ── Mosaic grid ──────────────────────────────────── */
          <div className="grid h-full w-full grid-cols-[1fr_0.45fr] gap-0.5">
            {/* Main image */}
            <div className="relative overflow-hidden">
              <Image
                src={caPreview ? mediaThumb(allImages[0], 420) : allImages[0]}
                alt={`${safeName} - ${campsiteType} in ${location}`}
                fill
                className="object-cover transition-transform duration-300 group-hover:scale-105"
                sizes="(max-width: 640px) 55vw, 180px"
                unoptimized
                onError={handleImageError}
              />
            </div>
            {/* Thumbnail column */}
            <div className={`grid gap-0.5 ${allImages.length >= 3 ? 'grid-rows-2' : ''}`}>
              <div className="relative overflow-hidden">
                <Image
                  src={caPreview ? mediaThumb(allImages[1], 200) : allImages[1]}
                  alt={`${safeName} photo 2`}
                  // A dead thumbnail (Bookeasy drops old images) hides rather than
                  // showing a broken-image icon; the main photo has its own fallback.
                  onError={hideBrokenImg}
                  fill
                  className="object-cover"
                  sizes="(max-width: 640px) 25vw, 80px"
                  unoptimized
                />
              </div>
              {allImages.length >= 3 && (
                <div className="relative overflow-hidden">
                  <Image
                    src={caPreview ? mediaThumb(allImages[2], 200) : allImages[2]}
                    alt={`${safeName} photo 3`}
                    // A dead thumbnail (Bookeasy drops old images) hides rather than
                    // showing a broken-image icon; the main photo has its own fallback.
                    onError={hideBrokenImg}
                    fill
                    className="object-cover"
                    sizes="(max-width: 640px) 25vw, 80px"
                    unoptimized
                  />
                  {totalGalleryCount > 3 && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                      <span className="text-[11px] font-bold text-white">
                        +{totalGalleryCount - 3}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (!hasOwnImage || imageError) && caPreview ? (
          <PlaceArt seed={String(entity.id ?? safeName)} type={entity.entity_type} label={campsiteType} size="lg" className="absolute inset-0" />
        ) : !hasOwnImage || imageError ? (
          <div className="absolute inset-0 bg-[linear-gradient(150deg,#eef4fb_0%,#e4efe8_100%)]">
            <TypeGlyph type={entity.entity_type} large />
          </div>
        ) : (
          /* ── Single image (logo-aware fit) ────────────────── */
          <Image
            src={caPreview ? mediaThumb(primaryImage, 720) : primaryImage}
            alt={`${safeName} - ${campsiteType} in ${location}`}
            fill
            className={
              heroFit === 'contain'
                ? 'object-contain p-3'
                : 'object-cover transition-transform duration-300 group-hover:scale-105'
            }
            sizes={ENTITY_CARD_SIZES}
            unoptimized
            onError={handleImageError}
            onLoad={handleHeroLoad}
          />
        )}

        {/* Gradient overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />

        {/* Open in new tab */}
        <button
          type="button"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (typeof window !== 'undefined') {
              window.open(entityUrl, '_blank', 'noopener,noreferrer');
            }
          }}
          className="absolute top-2 left-2 inline-flex items-center justify-center rounded-full bg-white/90 text-gray-700 hover:bg-[#1558b0] hover:text-white transition-colors w-8 h-8 shadow-sm opacity-0 group-hover:opacity-100 focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          aria-label={t('openNewTab', { name: safeName })}
        >
          <ArrowRightOutlined style={{ fontSize: '12px' }} />
        </button>

        {onAddToTrip && (
          <button
            type="button"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onAddToTrip(entity);
            }}
            // Solid fill + its own layer: the translucent backdrop-blur pill
            // composited as a blank white square over some photos (seen on a
            // premium card's mosaic), hiding the label.
            className={`absolute top-2 right-2 z-10 min-h-[28px] min-w-0 whitespace-nowrap px-2.5 py-1.5 text-[11px] font-semibold rounded-full shadow-sm transition-colors ${
              isInTrip
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-white text-gray-800 hover:bg-[#1558b0] hover:text-white'
            }`}
          >
            {isInTrip ? t('added') : t('addTrip')}
          </button>
        )}

        {/* Free camping badge */}
        {isFree && (
          <div className="absolute bottom-2 left-2">
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold text-white bg-green-600/90 shadow-sm backdrop-blur-sm">
              {t('free')}
            </span>
          </div>
        )}

        {/* Rating badge */}
        {entity.rating && !isNaN(Number(entity.rating)) && Number(entity.rating) > 0 && (
          <div className="absolute bottom-2 right-2">
            <span className="bg-white/90 text-gray-900 px-2 py-0.5 rounded-full text-[10px] font-bold flex items-center shadow-sm backdrop-blur-sm">
              <StarFilled className="mr-0.5" style={{ fontSize: '9px', color: '#f59e0b' }} />
              {entity.rating}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-2.5 flex-1 flex flex-col">
        <h3 className="font-bold text-gray-900 text-xs mb-1 line-clamp-1 leading-tight group-hover:text-[#1558b0] transition-colors">
          {displayName(safeName)}
        </h3>

        <div className="flex items-center text-[10px] text-gray-500 mb-1.5">
          <EnvironmentOutlined className="mr-0.5 flex-shrink-0" style={{ color: '#ef4444', fontSize: '10px' }} />
          <span className="truncate">{location}</span>
        </div>

        {/* Premium listings earn a short blurb — fuller detail than the compact default */}
        {entity.is_premium && (entity.short_desc || entity.desc) && (
          <p className="text-[10px] leading-snug text-gray-600 line-clamp-2 mb-1.5">
            {(() => {
              // stripHtml: RIDB (US) descriptions arrive as HTML fragments.
              const t = stripHtml(entity.short_desc || entity.desc || '');
              return t.length > 110 ? `${t.slice(0, 110).trimEnd()}…` : t;
            })()}
          </p>
        )}

        {keyFeatures.length > 0 && (
          <div className="flex items-center gap-1 mb-1.5">
            {keyFeatures.map(feature => (
              <div
                key={feature.id}
                className="w-5 h-5 flex items-center justify-center bg-gray-50 rounded text-gray-600 border border-gray-100"
                title={feature.desc || ''}
              >
                <span className="w-3 h-3">
                  <FeatureIcon feature={feature} />
                </span>
              </div>
            ))}
            {features.icons.length > 3 && (
              <span className="text-[10px] text-gray-400 font-medium">
                +{features.icons.length - 3}
              </span>
            )}
          </div>
        )}

        <div className="flex-1" />
        <div className="pt-1.5 border-t border-gray-100 mt-auto space-y-1.5">
          {highlightFeature && (
            <span className="text-[10px] text-gray-500 truncate block">
              {highlightFeature}
            </span>
          )}
          <div className="flex items-center gap-1.5">
            {isBookable ? (
              <a
                href={entity.booking_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="flex-1 text-center text-[10px] font-semibold text-white bg-green-600 hover:bg-green-700 rounded-md py-1.5 transition-colors"
              >
                {t('bookNow')}
              </a>
            ) : isFree ? (
              <span className="flex-1 text-center text-[10px] font-bold text-green-600 bg-green-50 rounded-md py-1.5 border border-green-100">
                {t('freeCamping')}
              </span>
            ) : null}
            {onViewDetails ? (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onViewDetails();
                }}
                className={`${isBookable || isFree ? '' : 'flex-1'} text-center text-[10px] font-semibold text-gray-700 bg-gray-100 hover:bg-[#1558b0] hover:text-white rounded-md py-1.5 px-3 transition-colors`}
              >
                {t('details')}
              </button>
            ) : (
              <Link
                href={entityUrl}
                className={`${isBookable || isFree ? '' : 'flex-1'} text-center text-[10px] font-semibold text-gray-700 bg-gray-100 hover:bg-[#1558b0] hover:text-white rounded-md py-1.5 px-3 transition-colors`}
                onClick={(e) => e.stopPropagation()}
              >
                {t('details')}
              </Link>
            )}
          </div>
        </div>
      </div>
    </article>
  );

  // COMPACT ROW LAYOUT — image left, details right. Used in the explorer's
  // results column beside the map, where the stacked card's photo meant only
  // ~1.5 results were visible. One photo (no mosaic), actions in the body so
  // nothing floats over the image.
  const hasRating = !!entity.rating && !isNaN(Number(entity.rating)) && Number(entity.rating) > 0;
  const premiumBlurb =
    entity.is_premium && (entity.short_desc || entity.desc)
      ? (() => {
          const text = stripHtml(entity.short_desc || entity.desc || '');
          return text.length > 110 ? `${text.slice(0, 110).trimEnd()}…` : text;
        })()
      : null;
  const listContent = (
    <article
      className={`ca-list-card group flex min-h-[128px] overflow-hidden rounded-2xl border bg-white transition-shadow duration-200 cursor-pointer hover:shadow-md ${
        isSelected ? 'ring-2 ring-accent shadow-md border-transparent' : 'border-gray-200 hover:border-gray-300'
      }`}
      onClick={onClick ? handleCardClick : undefined}
    >
      <div className="ca-list-card__media relative w-[124px] flex-none self-stretch overflow-hidden bg-[linear-gradient(150deg,#eef4fb_0%,#e4efe8_100%)] sm:w-[136px]">
        {/* Typed tile under the photo: a slow or missing image shows what
            kind of place it is, not an empty well or a stock photo. */}
        {caPreview && (!hasOwnImage || imageError) ? (
          // ca_preview: no photo of its own, so the store's no-picture
          // tile (warm paper, contour lines, what kind of place it is).
          <PlaceArt seed={String(entity.id ?? safeName)} type={entity.entity_type} label={isFree ? undefined : campsiteType} className="absolute inset-0" />
        ) : caPreview ? null : (
          <TypeGlyph type={entity.entity_type} />
        )}
        {hasOwnImage && !imageError && (
        <Image
          src={caPreview ? mediaThumb(primaryImage, 300) : primaryImage}
          alt={`${safeName} - ${campsiteType} in ${location}`}
          fill
          className={
            heroFit === 'contain'
              ? 'object-contain p-2'
              : 'object-cover transition-transform duration-300 group-hover:scale-105'
          }
          sizes="136px"
          // ca_preview: the store never shows an empty well while a photo
          // waits for the viewport (the phone list sits in its own scroller).
          loading={caPreview && eagerImage ? 'eager' : undefined}
          fetchPriority={caPreview && eagerImage ? 'high' : undefined}
          unoptimized
          onError={handleImageError}
          onLoad={handleHeroLoad}
        />
        )}
        {entity.is_premium && (
          <span className="ca-premium absolute left-1.5 top-1.5 inline-flex items-center gap-1 rounded bg-gradient-to-r from-amber-400 to-yellow-300 px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-[0.12em] text-amber-900 shadow-sm">
            <StarFilled style={{ fontSize: '8px' }} />
            {t('premium')}
          </span>
        )}
        {isFree && (
          <span className="ca-free absolute bottom-1.5 left-1.5 rounded-full bg-green-600 px-2 py-0.5 text-[10px] font-bold text-white shadow-sm">
            {t('free')}
          </span>
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col px-3 py-2.5">
        <div className="flex items-start gap-2">
          <h3 className={`ca-list-title min-w-0 flex-1 text-[15px] font-semibold leading-5 text-stone-900 ${caPreview ? 'line-clamp-2' : 'line-clamp-1'}`}>
            {displayName(safeName)}
          </h3>
          {hasRating && (
            <span className="flex flex-none items-center text-[13px] font-semibold text-stone-700">
              <StarFilled className="mr-0.5" style={{ fontSize: '10px', color: '#f59e0b' }} />
              {entity.rating}
            </span>
          )}
        </div>

        <div className="mt-1 flex items-center text-[13px] leading-5 text-stone-600">
          <EnvironmentOutlined className="mr-1 flex-shrink-0" style={{ color: '#a8a29e', fontSize: '12px' }} />
          <span className="truncate">{location}</span>
        </div>

        {premiumBlurb && !(caPreview && premiumBlurb.toLowerCase().startsWith(safeName.toLowerCase().slice(0, 16))) ? (
          <p className="mt-1 text-[13px] leading-5 text-stone-600 line-clamp-1">{premiumBlurb}</p>
        ) : keyFeatures.length > 0 ? (
          <div className="ca-list-feat mt-1 flex h-5 items-center gap-1">
            {keyFeatures.map((feature) => (
              <div
                key={feature.id}
                className="flex h-5 w-5 items-center justify-center rounded border border-gray-100 bg-gray-50 text-gray-600"
                title={feature.desc || ''}
              >
                <span className="h-3 w-3">
                  <FeatureIcon feature={feature} />
                </span>
              </div>
            ))}
            {features.icons.length > 3 && (
              <span className="text-[12px] font-medium text-stone-500">+{features.icons.length - 3}</span>
            )}
          </div>
        ) : campsiteType !== 'Campsite' ? (
          // No amenity icons: say what it is, unless that's just "Campsite"
          // again (it repeated on every card in a list of campsites).
          <p className="mt-1 h-5 truncate text-[13px] leading-5 text-stone-500">{campsiteType}</p>
        ) : null}

        <div className="mt-auto flex items-center gap-1.5 pt-2">
          {onAddToTrip && (
            <button
              type="button"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onAddToTrip(entity);
              }}
              className={`ca-addtrip inline-flex h-9 min-h-0 min-w-0 items-center whitespace-nowrap rounded-lg px-3 text-[13px] font-semibold transition-colors border ${
                isInTrip
                  ? 'border-green-600 bg-green-600 text-white hover:bg-green-700'
                  : 'border-[#1558b0]/40 bg-[#1558b0]/[.06] text-[#1558b0] hover:border-[#1558b0] hover:bg-[#1558b0] hover:text-white'
              }`}
            >
              {isInTrip ? t('added') : t('addTrip')}
            </button>
          )}
          {isBookable ? (
            <a
              href={entity.booking_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="ca-list-book inline-flex h-9 min-h-0 min-w-0 items-center whitespace-nowrap rounded-lg px-3 text-[13px] font-semibold transition-colors border border-stone-200 bg-white text-stone-800 hover:border-stone-300 hover:bg-stone-50"
            >
              {t('bookNow')}
            </a>
          ) : null}
          <div className="flex-1" />
          {onViewDetails ? (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onViewDetails();
              }}
              className="ca-list-details inline-flex h-9 min-h-0 min-w-0 items-center whitespace-nowrap rounded-lg px-2.5 text-[13px] font-medium text-stone-600 transition-colors hover:bg-stone-100 hover:text-stone-900"
            >
              {t('details')}
            </button>
          ) : (
            <Link
              href={entityUrl}
              className="ca-list-details inline-flex h-9 min-h-0 min-w-0 items-center whitespace-nowrap rounded-lg px-2.5 text-[13px] font-medium text-stone-600 transition-colors hover:bg-stone-100 hover:text-stone-900"
              onClick={(e) => e.stopPropagation()}
            >
              {t('details')}
            </Link>
          )}
        </div>
      </div>
    </article>
  );

  if (layout === 'list') {
    if (onClick) return listContent;
    return (
      <Link href={entityUrl} className="block">
        {listContent}
      </Link>
    );
  }

  // If onClick is provided, just render the card content
  if (onClick) {
    return <CardContent />;
  }

  // Otherwise wrap in Link for SEO-friendly navigation
  return (
    <Link href={entityUrl} className="block">
      <CardContent />
    </Link>
  );
};

export default EntityCard;


/** Tent for camps, caravan for caravan parks, a roof for stays: drawn, not stock. */
function TypeGlyph({ type, large = false }: { type?: string | null; large?: boolean }) {
  const t = (type || '').toLowerCase();
  const cls = `absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-[#1558b0]/40 ${large ? 'h-12 w-12' : 'h-9 w-9'}`;
  const common = {
    viewBox: '0 0 24 24',
    'aria-hidden': true,
    className: cls,
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.6,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  if (t.includes('caravan')) {
    return (
      <svg {...common}>
        <path d="M3.5 16.5V8.5A2 2 0 015.5 6.5h10a4.5 4.5 0 014.5 4.5v5.5h-16.5zM20 16.5h1.5" />
        <circle cx="9" cy="17.5" r="1.8" />
        <path d="M6.5 9.5h3.5v3h-3.5zM13 9.5h3v7" />
      </svg>
    );
  }
  if (t.includes('accom') || t.includes('tour')) {
    return (
      <svg {...common}>
        <path d="M4 11l8-6.5 8 6.5M6 9.5V19.5h12V9.5M10 19.5v-5h4v5" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path d="M3 19.5L12 5l9 14.5zM12 5v14.5M9 19.5l3-5 3 5" />
    </svg>
  );
}
